import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Autocomplete,
  CircularProgress,
  TextField,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { usePaginatedSearch } from "hooks/usePaginatedSearch";
import { getPaginatedClientData } from "utils/clientService";

const fetchClientPage = ({ page, size, search }) =>
  getPaginatedClientData({ pageIndex: page, pageSize: size, searchText: search });

const AddAccountsModal = ({ open, onClose, accountsList }) => {
  const navigate = useNavigate();
  const [selectedClient, setSelectedClient] = useState(null);
  const { inputValue, setInputValue, debouncedSearch, options, loading, listboxProps } =
    usePaginatedSearch(fetchClientPage, { pageSize: 20, enabled: !!open });

  const handleProceed = () => {
    if (!selectedClient) return;
    onClose();
    setSelectedClient(null);
    navigate("/admin/account/add", {
      state: {
        item: { clientData: selectedClient, hasAccountDetails: false, clientAccountId: null },
      },
    });
  };

  const handleClose = () => {
    setSelectedClient(null);
    onClose();
  };

  return (
    <Dialog open={!!open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle className="font-semibold text-app-text">Add account</DialogTitle>
      <DialogContent className="pt-2">
        <p className="text-sm text-app-text-secondary mb-3">
          Select a client to create an account.
        </p>
        <Autocomplete
          value={selectedClient}
          onChange={(_, newValue) => {
            setSelectedClient(newValue);
            if (newValue?.name) setInputValue(newValue.name);
          }}
          options={options}
          loading={loading}
          filterOptions={(x) => x}
          inputValue={inputValue}
          onInputChange={(_, newInputValue, reason) => {
            if (reason === "reset") return;
            setInputValue(newInputValue);
          }}
          getOptionLabel={(option) =>
            option?.name
              ? `${option.name}${option.passportNumber ? ` · ${option.passportNumber}` : ""}`
              : ""
          }
          noOptionsText={
            loading
              ? "Searching…"
              : debouncedSearch
              ? "No matching clients"
              : "Type to search clients"
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label="Client"
              placeholder="Search by name or passport..."
              size="small"
              variant="outlined"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loading ? <CircularProgress color="inherit" size={16} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          renderOption={(props, option) => {
            return (
              <li {...props} key={option?.id + option?.email + option?.name}>
                <div className="flex items-center justify-between gap-2 w-full">
                  <div className="flex items-center gap-2">
                    {option?.passportNumber
                      ? `${option?.name} (${option?.passportNumber})`
                      : option?.name || ""}
                  </div>
                </div>
              </li>
            );
          }}
          ListboxProps={{ style: { maxHeight: "220px" }, ...listboxProps }}
        />
      </DialogContent>
      <DialogActions className="gap-2 px-6 pb-4">
        <Button onClick={handleClose} color="inherit">
          Cancel
        </Button>
        <Button variant="contained" onClick={handleProceed} disabled={!selectedClient}>
          Proceed
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddAccountsModal;
